import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { APP_CONSTANTS } from '@/lib/constants/app'
import { Shirt, Hash, Type } from 'lucide-react'
import type { UseFormRegister, FieldErrors } from 'react-hook-form'
import type { RegistrationFormData } from '@/lib/types/registration'

interface TShirtDetailsSectionProps {
  register: UseFormRegister<RegistrationFormData>
  errors: FieldErrors<RegistrationFormData>
}

export function TShirtDetailsSection({ register, errors }: TShirtDetailsSectionProps) {
  return (
    <Card variant="premium">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Shirt className="w-5 h-5 text-primary" />
          </div>
          <div>
            <CardTitle>Jersey Details</CardTitle>
            <p className="text-sm text-slate-500 mt-0.5">Customize your tournament jersey</p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Jersey Size */}
          <div>
            <Label htmlFor="tshirtSize" className="flex items-center gap-2 mb-2">
              <Shirt className="w-4 h-4 text-slate-400" />
              Jersey Size
              <span className="text-destructive">*</span>
            </Label>
            <Select
              id="tshirtSize"
              {...register('tshirtSize')}
              className={errors.tshirtSize ? 'border-destructive focus:border-destructive' : ''}
            >
              <option value="">Select Size</option>
              {APP_CONSTANTS.TSHIRT_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </Select>
            {errors.tshirtSize && (
              <p className="text-sm text-destructive mt-1.5 flex items-center gap-1">
                <span className="inline-block w-1 h-1 rounded-full bg-destructive" />
                {errors.tshirtSize.message}
              </p>
            )}
          </div>

          {/* Jersey Number */}
          <div>
            <Label htmlFor="tshirtNumber" className="flex items-center gap-2 mb-2">
              <Hash className="w-4 h-4 text-slate-400" />
              Jersey Number
              <span className="text-destructive">*</span>
            </Label>
            <Input
              id="tshirtNumber"
              {...register('tshirtNumber')}
              placeholder="e.g., 10"
              maxLength={2}
              inputMode="numeric"
              pattern="[0-9]*"
              onKeyDown={(e) => {
                // Allow: backspace, delete, tab, escape, enter, arrows
                if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  return
                }
                // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) {
                  return
                }
                // Allow only numbers
                if (!/^\d$/.test(e.key)) {
                  e.preventDefault()
                }
              }}
              onInput={(e) => {
                // Remove any non-numeric characters and limit to 2 digits
                const target = e.target as HTMLInputElement
                target.value = target.value.replace(/\D/g, '').slice(0, 2)
              }}
              className={errors.tshirtNumber ? 'border-destructive focus:border-destructive' : ''}
            />
            {errors.tshirtNumber && (
              <p className="text-sm text-destructive mt-1.5 flex items-center gap-1">
                <span className="inline-block w-1 h-1 rounded-full bg-destructive" />
                {errors.tshirtNumber.message}
              </p>
            )}
          </div>

          {/* Name on Jersey */}
          <div>
            <Label htmlFor="tshirtName" className="flex items-center gap-2 mb-2">
              <Type className="w-4 h-4 text-slate-400" />
              Name on Jersey
              <span className="text-destructive">*</span>
            </Label>
            <Input
              id="tshirtName"
              {...register('tshirtName')}
              placeholder="e.g., SHARMA"
              className={errors.tshirtName ? 'border-destructive focus:border-destructive' : ''}
            />
            {errors.tshirtName && (
              <p className="text-sm text-destructive mt-1.5 flex items-center gap-1">
                <span className="inline-block w-1 h-1 rounded-full bg-destructive" />
                {errors.tshirtName.message}
              </p>
            )}
          </div>
        </div>

      </CardContent>
    </Card>
  )
}
